import React, { useEffect } from 'react';
import {
  Modal,
  Form,
  Input,
  DatePicker,
  Switch,
  Button,
  Typography,
  Select,
} from 'antd';
import dayjs from 'dayjs';
import { observer } from 'mobx-react-lite';
import certificateStore from '../../store/CertificateStore';
import employeeStore from '../../store/EmployeeStore';
import { useBitrix } from '../../context/BitrixContext';
import TagSelector from '../Tags/TagSelector';

const { Title } = Typography;
const { Option } = Select;

const API_URL = 'https://bitrixapi.tmg-din-reestr.ru'; // адрес бекенда

const CertificateModalForm = observer(({ open, onClose, certificate = null }) => {
  const [form] = Form.useForm();
  const isPermanent = Form.useWatch('isPermanent', form);

  const { user, auth } = useBitrix();


  useEffect(() => {
  if (open) {
    employeeStore.fetchEmployees({ user: user?.ID, auth });
    if (certificate) {
      form.setFieldsValue({
        name: certificate.name || '',
        employeeId: certificate.employeeId || undefined,
        isPermanent: !certificate.hasDeadline,
        expired: certificate.expired ? dayjs(certificate.expired) : null,
        bitrixFolder: certificate.bitrixFolder || '',
        links: (certificate.files || []).map((url) => ({ url })),
        info: certificate.info || '',
        tagIds: certificate.tagIds || [],
      });
    } else {
      form.setFieldsValue({
        name: '',
        employeeId: undefined,
        isPermanent: false,
        expired: null,
        bitrixFolder: '',
        links: [{ url: '' }],
        info: '',
        tagIds: [],
      });
    }
  } else {
    form.resetFields();
  }
}, [open, certificate, form, user, auth]);


  const handleFinish = async (values) => {
    const selectedEmployee = employeeStore.employees.find(
      (e) => e.id === values.employeeId
    );

    const payload = {
      name: values.name,
      employeeId: values.employeeId,
      employeeName: selectedEmployee?.name || '',
      hasDeadline: !values.isPermanent,
      expired: values.isPermanent ? null : values.expired?.format('YYYY-MM-DD'),
      bitrixFolder: values.bitrixFolder,
      info: values.info,
      files: values.links?.map((l) => l.url) || [],
      tagIds: values.tagIds || [],
    };

    try {
      if (certificate?.id) {
        await certificateStore.updateCertificate(certificate.id, payload);
      } else {
        await certificateStore.addCertificate(payload);
      }
      onClose();
    } catch (err) {
      console.error('Ошибка при сохранении удостоверения:', err);
    }
  };

  return (
    <Modal
      open={open}
      onCancel={onClose}
      onOk={() => form.submit()}
      title={certificate ? 'Редактировать удостоверение' : 'Добавить удостоверение'}
      okText="Сохранить"
      cancelText="Отмена"
      destroyOnClose
      styles={{
        body: { maxHeight: '70vh', overflowY: 'auto' }
      }}
    >
      <Form layout="vertical" form={form} onFinish={handleFinish} autoComplete="off">
        <Form.Item
          label="Название"
          name="name"
          rules={[{ required: true, message: 'Введите название' }]}
        >
          <Input maxLength={100} showCount placeholder="Например, Свидетельство по ТБ" />
        </Form.Item>

        <Form.Item
          label="Сотрудник"
          name="employeeId"
          rules={[{ required: true, message: 'Выберите сотрудника' }]}
        >
          <Select
            placeholder="Выберите сотрудника"
            showSearch
            allowClear
            optionFilterProp="children"
            getPopupContainer={(trigger) => trigger.closest('.ant-modal-body') || document.body}
            filterOption={(input, option) =>
              option.children.toLowerCase().includes(input.toLowerCase())
            }
          >
            {employeeStore.employees.map((emp) => (
              <Option key={emp.id} value={emp.id}>
                {emp.name}
              </Option>
            ))}
          </Select>
        </Form.Item>

        <Form.Item label="Бессрочно" name="isPermanent" valuePropName="checked">
          <Switch />
        </Form.Item>

        {!isPermanent && (
          <Form.Item
            label="Дата истечения"
            name="expired"
            rules={[{ required: true, message: 'Укажите дату истечения' }]}
          >
            <DatePicker style={{ width: '100%' }} format="DD.MM.YYYY" />
          </Form.Item>
        )}

        <Form.Item label="Описание" name="info">
          <Input.TextArea placeholder="Дополнительная информация" rows={3} />
        </Form.Item>

        <Form.Item label="Теги" name="tagIds">
          <TagSelector />
        </Form.Item>

        <Form.Item label="Ссылка на папку Bitrix" name="bitrixFolder">
          <Input placeholder="https://bitrix24.example.com/folder" />
        </Form.Item>
      </Form>
    </Modal>
  );
});

export default CertificateModalForm;
